import { Data, Match } from 'effect';
import { useCallback, useContext, useEffect, useReducer, useState } from 'react';
import type { ActionEnum, AdvisoryFeeConfig as ApiAdvisoryFeeConfig } from 'venn-api';
import { getAdvisoryFeeConfig, saveAdvisoryFeeConfig } from 'venn-api';
import { UserContext } from 'venn-components';
import { sendSignal } from 'venn-state';

export type AdvisoryFeeConfig = {
  defaultFee: number;
  minFee: number;
  maxFee: number;
  displayZeroFee: boolean;
};

export type AdvisoryFeeAction = Data.TaggedEnum<{
  SetDefaultFee: { value: number };
  SetMinFee: { value: number };
  SetMaxFee: { value: number };
  ToggleDisplayZeroFee: {};
  Initialize: { config: AdvisoryFeeConfig };
  ShowConfirmation: {};
  HideConfirmation: {};
  Save: {};
}>;

export const AdvisoryFeeAction = Data.taggedEnum<AdvisoryFeeAction>();

const DEFAULT_ADVISORY_FEE_CONFIG: AdvisoryFeeConfig = {
  defaultFee: 0,
  minFee: 0,
  maxFee: 100,
  displayZeroFee: false,
};

type ReducerAction = Exclude<
  AdvisoryFeeAction,
  { _tag: 'Save' } | { _tag: 'ShowConfirmation' } | { _tag: 'HideConfirmation' }
>;

const advisoryFeeReducer = (state: AdvisoryFeeConfig, action: ReducerAction): AdvisoryFeeConfig =>
  Match.value(action).pipe(
    Match.tag('SetDefaultFee', ({ value }) => ({ ...state, defaultFee: value })),
    Match.tag('SetMinFee', ({ value }) => ({ ...state, minFee: value })),
    Match.tag('SetMaxFee', ({ value }) => ({ ...state, maxFee: value })),
    Match.tag('ToggleDisplayZeroFee', () => ({ ...state, displayZeroFee: !state.displayZeroFee })),
    Match.tag('Initialize', ({ config }) => config),
    Match.exhaustive,
  );

const hasConfigChanged = (current: AdvisoryFeeConfig, initial: AdvisoryFeeConfig): boolean =>
  current.defaultFee !== initial.defaultFee ||
  current.minFee !== initial.minFee ||
  current.maxFee !== initial.maxFee ||
  current.displayZeroFee !== initial.displayZeroFee;

const FIELD_LABEL = {
  MIN: 'Min',
  DEFAULT: 'Default',
  MAX: 'Max',
} as const;

/**
 * Validates that minFee ≤ defaultFee ≤ maxFee.
 * Returns an object with error messages for fields that violate the order.
 */
const getValidationErrors = (config: AdvisoryFeeConfig): Partial<Record<keyof AdvisoryFeeConfig, string>> => {
  // Accumulates "greater than" and "less than" constraints per field,
  // allowing multiple violations to be merged into a single message.
  const error: Record<
    'defaultFee' | 'minFee' | 'maxFee',
    {
      greater: string[];
      less: string[];
    }
  > = {
    minFee: { greater: [], less: [] },
    defaultFee: { greater: [], less: [] },
    maxFee: { greater: [], less: [] },
  };

  // Each rule is checked independently so that fields involved in multiple
  // violations accumulate all relevant constraints, e.g. maxFee could end up
  // with greater: ['Min', 'Default'], producing "Cannot be less than Min and Default".

  if (config.minFee > config.defaultFee) {
    error.defaultFee.greater.push(FIELD_LABEL.MIN);
    error.minFee.less.push(FIELD_LABEL.DEFAULT);
  }

  if (config.defaultFee > config.maxFee) {
    error.maxFee.greater.push(FIELD_LABEL.DEFAULT);
    error.defaultFee.less.push(FIELD_LABEL.MAX);
  }

  // Checked separately from the two rules above to catch the case where
  // minFee > maxFee without defaultFee being the intermediary violation.
  if (config.minFee > config.maxFee) {
    error.maxFee.greater.push(FIELD_LABEL.MIN);
    error.minFee.less.push(FIELD_LABEL.MAX);
  }

  // Build a single, human-readable message per field from its accumulated constraints.
  return Object.entries(error).reduce<Partial<Record<keyof AdvisoryFeeConfig, string>>>(
    (acc, [field, { greater, less }]) => {
      let message: string | undefined;

      if (greater.length && less.length) {
        // e.g. "Cannot be less than Min or greater than Max"
        message = `Cannot be less than ${greater.join(' and ')} or greater than ${less.join(' and ')}`;
      } else if (greater.length) {
        // e.g. "Cannot be less than Min and Default"
        message = `Cannot be less than ${greater.join(' and ')}`;
      } else if (less.length) {
        // e.g. "Cannot be greater than Default and Max"
        message = `Cannot be greater than ${less.join(' and ')}`;
      }

      if (message) {
        acc[field as keyof AdvisoryFeeConfig] = message;
      }

      return acc;
    },
    {},
  );
};

const isStatusError = (error: unknown, status: number): boolean =>
  typeof error === 'object' && error !== null && 'status' in error && (error as { status: number }).status === status;

const toLocalConfig = (api: Partial<ApiAdvisoryFeeConfig>): AdvisoryFeeConfig => ({
  defaultFee: api.defaultFee ?? DEFAULT_ADVISORY_FEE_CONFIG.defaultFee,
  minFee: api.minFee ?? DEFAULT_ADVISORY_FEE_CONFIG.minFee,
  maxFee: api.maxFee ?? DEFAULT_ADVISORY_FEE_CONFIG.maxFee,
  displayZeroFee: api.displayZeroFee ?? DEFAULT_ADVISORY_FEE_CONFIG.displayZeroFee,
});

type Status = 'loading' | 'idle' | 'saving' | 'forbidden';

export const useAdvisoryFeesSection = () => {
  const { hasPermission } = useContext(UserContext);
  const [config, dispatch] = useReducer(advisoryFeeReducer, DEFAULT_ADVISORY_FEE_CONFIG);

  // initialConfig stores the persisted state on load/save to track unsaved changes.
  const [initialConfig, setInitialConfig] = useState(DEFAULT_ADVISORY_FEE_CONFIG);

  // lastValidConfig stores the last error-free state to filter which fields show errors.
  const [lastValidConfig, setLastValidConfig] = useState(DEFAULT_ADVISORY_FEE_CONFIG);

  const [status, setStatus] = useState<Status>('loading');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const isReadOnly = !hasPermission('MANAGE_ADVISORY_FEE_CONFIG' as ActionEnum) || status === 'forbidden';
  const hasUnsavedChanges = hasConfigChanged(config, initialConfig);
  const allErrors = getValidationErrors(config);
  const isValid = Object.keys(allErrors).length === 0;

  // Derived validation errors filtered by what has changed since the last "valid" configuration.
  // This avoids showing cascading errors for fields the user hasn't modified yet.
  const errors = Object.fromEntries(
    Object.entries(allErrors).filter(
      ([field]) => config[field as keyof AdvisoryFeeConfig] !== lastValidConfig[field as keyof AdvisoryFeeConfig],
    ),
  );

  const canSave = hasUnsavedChanges && !isReadOnly && status === 'idle' && isValid;

  // Whenever the current configuration becomes valid, update the baseline for error filtering.
  useEffect(() => {
    if (isValid) {
      setLastValidConfig(config);
    }
  }, [config, isValid]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { content } = await getAdvisoryFeeConfig();
        if (cancelled) {
          return;
        }

        const loaded = toLocalConfig(content);
        setInitialConfig(loaded);
        setLastValidConfig(loaded);
        dispatch(AdvisoryFeeAction.Initialize({ config: loaded }));
        setStatus('idle');
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }

        setStatus(isStatusError(error, 403) ? 'forbidden' : 'idle');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async () => {
    setStatus('saving');
    try {
      const { content } = await saveAdvisoryFeeConfig(config);
      const saved = toLocalConfig(content);
      setInitialConfig(saved);
      setLastValidConfig(saved);
      dispatch(AdvisoryFeeAction.Initialize({ config: saved }));
      sendSignal({ type: 'WorkspaceConfigurationUpdate' });
    } finally {
      setStatus('idle');
    }
  }, [config]);

  const performAction = useCallback(
    (action: AdvisoryFeeAction) =>
      Match.value(action).pipe(
        Match.tag('SetDefaultFee', (a) => dispatch(a)),
        Match.tag('SetMinFee', (a) => dispatch(a)),
        Match.tag('SetMaxFee', (a) => dispatch(a)),
        Match.tag('ToggleDisplayZeroFee', (a) => dispatch(a)),
        Match.tag('Initialize', (a) => dispatch(a)),
        Match.tag('ShowConfirmation', () => setShowConfirmation(true)),
        Match.tag('HideConfirmation', () => setShowConfirmation(false)),
        Match.tag('Save', () => {
          setShowConfirmation(false);
          save();
        }),
        Match.exhaustive,
      ),
    [save],
  );

  return { config, performAction, canSave, isReadOnly, status, initialConfig, showConfirmation, errors, isValid };
};
